import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

export interface AdminUser {
  id: string;
  user_id: string;
  role: 'admin' | 'superadmin';
  created_at: string;
  email: string;
  first_name: string;
  last_name: string;
}

export function useAdminManagement() {
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAdmins = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data, error: rpcError } = await supabase.rpc('admin_list_admins');
      if (rpcError) throw rpcError;
      setAdmins(data || []);
    } catch (e: any) {
      setError(e.message || 'Failed to fetch admins');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const addAdmin = useCallback(
    async (email: string, role: 'admin' | 'superadmin' = 'admin') => {
      setError(null);
      try {
        const { error: rpcError } = await supabase.rpc('admin_add_admin', {
          target_email: email,
          target_role: role,
        });
        if (rpcError) throw rpcError;
        await fetchAdmins();
        return { success: true };
      } catch (e: any) {
        const msg = e.message || 'Failed to add admin';
        setError(msg);
        return { success: false, error: msg };
      }
    },
    [fetchAdmins]
  );

  const removeAdmin = useCallback(
    async (userId: string) => {
      setError(null);
      try {
        const { error: rpcError } = await supabase.rpc('admin_remove_admin', {
          target_user_id: userId,
        });
        if (rpcError) throw rpcError;
        await fetchAdmins();
        return { success: true };
      } catch (e: any) {
        const msg = e.message || 'Failed to remove admin';
        setError(msg);
        return { success: false, error: msg };
      }
    },
    [fetchAdmins]
  );

  const updateRole = useCallback(
    async (userId: string, newRole: 'admin' | 'superadmin') => {
      setError(null);
      try {
        const { error: rpcError } = await supabase.rpc('admin_update_role', {
          target_user_id: userId,
          new_role: newRole,
        });
        if (rpcError) throw rpcError;
        await fetchAdmins();
        return { success: true };
      } catch (e: any) {
        const msg = e.message || 'Failed to update role';
        setError(msg);
        return { success: false, error: msg };
      }
    },
    [fetchAdmins]
  );

  return { admins, isLoading, error, fetchAdmins, addAdmin, removeAdmin, updateRole };
}
